import React from 'react';
import { Card, Table, Button, Modal, Form, Input, Checkbox, message, Tag, Space } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { deliveryAddressesAPI } from '../services/api';

const DeliveryAddresses: React.FC = () => {
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [data, setData] = React.useState<any[]>([]);
  const [modalVisible, setModalVisible] = React.useState(false);
  const [editingId, setEditingId] = React.useState<number | null>(null);
  const [form] = Form.useForm();

  const load = async () => {
    setLoading(true);
    try {
      const addresses = await deliveryAddressesAPI.list();
      setData(addresses || []);
    } catch (e: any) {
      console.error('Failed to load addresses', e);
      message.error(e?.response?.data?.error || 'Ошибка загрузки адресов');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    form.resetFields();
    setEditingId(null);
    setModalVisible(true);
  };

  const openEdit = (record: any) => {
    form.setFieldsValue(record);
    setEditingId(record.id);
    setModalVisible(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      if (editingId) {
        await deliveryAddressesAPI.update(editingId, values);
        message.success('Адрес обновлен');
      } else {
        await deliveryAddressesAPI.create(values);
        message.success('Адрес добавлен');
      }
      setModalVisible(false);
      load();
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      console.error('Failed to save address', error);
      message.error(error?.response?.data?.error || 'Не удалось сохранить адрес');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (id: number) => {
    Modal.confirm({
      title: 'Удалить адрес?',
      content: 'Это действие нельзя отменить',
      onOk: async () => {
        try {
          await deliveryAddressesAPI.remove(id);
          message.success('Адрес удален');
          load();
        } catch (error: any) {
          console.error('Failed to delete address', error);
          message.error(error?.response?.data?.error || 'Не удалось удалить адрес');
        }
      }
    });
  };

  const columns = [
    {
      title: 'Адрес',
      dataIndex: 'addressLine1',
      key: 'addressLine1'
    },
    {
      title: 'Город',
      dataIndex: 'city',
      key: 'city'
    },
    {
      title: 'Индекс',
      dataIndex: 'postalCode',
      key: 'postalCode'
    },
    {
      title: 'Примечания',
      dataIndex: 'notes',
      key: 'notes',
      render: (notes: string | null) => notes || '—'
    },
    {
      title: 'По умолчанию',
      dataIndex: 'isDefault',
      key: 'isDefault',
      render: (isDefault: boolean) =>
        isDefault ? (
          <Tag color="green" icon={<CheckCircleOutlined />}>
            По умолчанию
          </Tag>
        ) : (
          <Tag>Нет</Tag>
        )
    },
    {
      title: 'Действия',
      key: 'actions',
      render: (_: any, record: any) => (
        <Space>
          <Button icon={<EditOutlined />} size="small" onClick={() => openEdit(record)}>
            Редактировать
          </Button>
          <Button
            icon={<DeleteOutlined />}
            size="small"
            danger
            onClick={() => handleDelete(record.id)}
          >
            Удалить
          </Button>
        </Space>
      )
    }
  ];

  return (
    <Card
      title="Адреса доставки"
      loading={loading}
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          Добавить адрес
        </Button>
      }
    >
      <Table
        rowKey="id"
        columns={columns as any}
        dataSource={data}
        pagination={false}
        locale={{ emptyText: 'Нет адресов доставки. Добавьте первый адрес.' }}
      />

      <Modal
        title={editingId ? 'Редактировать адрес' : 'Добавить адрес'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={handleSave}
        okText="Сохранить"
        cancelText="Отмена"
        confirmLoading={saving}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="addressLine1"
            label="Адрес (строка 1)"
            rules={[{ required: true, message: 'Введите адрес' }]}
          >
            <Input placeholder="ул. Ленина, д. 10" />
          </Form.Item>

          <Form.Item name="addressLine2" label="Адрес (строка 2)">
            <Input placeholder="кв. 15" />
          </Form.Item>

          <Form.Item
            name="city"
            label="Город"
            rules={[{ required: true, message: 'Введите город' }]}
          >
            <Input placeholder="Москва" />
          </Form.Item>

          <Form.Item name="postalCode" label="Почтовый индекс">
            <Input placeholder="101000" />
          </Form.Item>

          <Form.Item name="notes" label="Примечания">
            <Input.TextArea rows={3} placeholder="Например, вход со двора" />
          </Form.Item>

          <Form.Item name="isDefault" valuePropName="checked" initialValue={false}>
            <Checkbox>Использовать по умолчанию</Checkbox>
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default DeliveryAddresses;

